import React, {useCallback, useState} from "react";
import ReactCreditCards from "../../../../custom/react-credit-cards";
import {getCardMonth} from "../../../../../core/services/utils/utils";
import {CustomerPaymentMethodTypeNames} from "../../../../../core/constants/enums";
import classnames from "classnames";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import Form from "../../../base/form";
import {OutlinedInputForm} from "../../../base/input/outlined_input";
import {Col, Row} from "reactstrap";
import {DialogActions, DialogContent, Divider} from "@mui/material";
import Button from "../../../base/button/button";
import Grid2 from "@mui/material/Unstable_Grid2";
import {MuiRFF, Yup} from '../../../form';
import useIsMounted from "../../../../hooks/use-is-mounted";

const formNames = {
    amount: 'amount',
    description: 'description',
}

const schema = Yup.object().shape({
    [formNames.amount]: Yup.number().moreThan(0.00, "Amount must be greater than zero").nullable().required(ValidateMessages.required).typeError("Invalid Amount"),
    [formNames.description]: Yup.string().trim().nullable(),
})

const validate = MuiRFF.makeValidate(schema)
const required = MuiRFF.makeRequired(schema)

const ChargeCreditCardForm = ({card, customerId, onCharge, close}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Prepares the api request based on the values
     * and calls the onCharge callback function passed in the props
     * @param values
     */
    const onSubmit = useCallback(async (values) => {
        const forApi = {
            cardId: card.id,
            PaymentProvider: card.providerName,
            customerId: customerId,
            amount: parseFloat(values[formNames.amount]),
            description: values[formNames.description],
        }
        setSubmitting(true);
        await onCharge(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
    }, [card?.id, card?.providerName, customerId, onCharge, isMounted]);

    return (
        <>
            <DialogContent>
                <Form
                    className={'py-3 px-3'}
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={{}}
                    id={'charge-credit-card-form'}
                    render={({values, form}) => {
                        return (
                            <>
                                <Grid2 container spacing={2}>
                                    <Grid2 xs={12} md={'auto'}>
                                        <div className={classnames({'position-relative': card?.isDefault})}>
                                            <ReactCreditCards
                                                preview
                                                cvc={'***'}
                                                expiry={`${getCardMonth(card?.month)}`}
                                                focused={'name'}
                                                name={card?.isDefault ? 'Default Card' : ' '}
                                                number={`************${card?.lastFourDigits}`}
                                                issuer={CustomerPaymentMethodTypeNames[card?.type] ?? 'unknown'}
                                            />
                                        </div>
                                    </Grid2>
                                    <Grid2 xs={12} md>
                                        <div className={'w-100 d-flex flex-wrap 2-100'}>
                                            <Col xs={12} className={'mb-3'}>
                                                <OutlinedInputForm
                                                    InputProps={{startAdornment: '$'}}
                                                    name={formNames.amount}
                                                    placeholder={''}
                                                    label={"Amount"}
                                                    type={'number'}
                                                    required={required[formNames.amount]}
                                                    value={values[formNames.amount] ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={12}>
                                                <OutlinedInputForm
                                                    multiline={true}
                                                    minRows={4}
                                                    name={formNames.description}
                                                    placeholder={'Type...'}
                                                    label={"Description"}
                                                    required={required[formNames.description]}
                                                    value={values[formNames.description] ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                            </Col>
                                        </div>
                                    </Grid2>
                                </Grid2>
                            </>
                        )
                    }}
                />
            </DialogContent>
            <DialogActions className={'p-0 w-100'}>
                <Row className={'w-100 no-gutters'}>
                    <Divider className='divider'/>
                    <Row className='no-gutters justify-content-between align-items-center w-100'>
                        <Button
                            disabled={submitting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={close}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Close
                        </Button>
                        <Button
                            disabled={submitting}
                            variant={'dialog'}
                            color={'primary'}
                            type={'submit'}
                            form={'charge-credit-card-form'}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {
                                submitting
                                    ? 'Charging...'
                                    : "Charge"
                            }
                        </Button>
                    </Row>
                </Row>
            </DialogActions>
        </>
    );
}

export default ChargeCreditCardForm;
